import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Table, Button, Modal, Spinner } from '@/components/ui';
import { qpTrackingTaskUrl } from '../../work-id/utils/taskPageUrl';
import { REPORT_HEADER_BG } from '../utils/reportTable';
import type { CostBreakdownTask, TaskCostBreakdown } from '../../../services/reportService';

const COST_COLUMNS: { key: keyof TaskCostBreakdown; label: string; title: string }[] = [
  { key: 'labour', label: 'Labour', title: 'Employee time' },
  { key: 'material', label: 'Material', title: 'Materials' },
  { key: 'equipment', label: 'Equip.', title: 'Company vehicles and equipment' },
  { key: 'hiredEquipment', label: 'HE', title: 'Hired equipment' },
  { key: 'subcontractor', label: 'Subs', title: 'Subcontractors' },
  { key: 'other', label: 'Other', title: 'Other costs' }
];

const money = (value: number) =>
  value === 0 ? '-' : (value < 0 ? '-$' : '$') + Math.abs(Math.round(value)).toLocaleString('en-US');

interface CostBreakdownModalProps {
  show: boolean;
  onHide: () => void;
  title: React.ReactNode;
  tasks: CostBreakdownTask[];
  loading: boolean;
  error: string | null;
}

/**
 * Quantified Costing drill-down: each task behind an activity row with its value, cost split by
 * type and gross margin, biggest loss first, so an unprofitable job stands out.
 */
const CostBreakdownModal: React.FC<CostBreakdownModalProps> = ({ show, onHide, title, tasks, loading, error }) => {
  const ready = !loading && !error;
  const sum = (pick: (t: CostBreakdownTask) => number) => tasks.reduce((s, t) => s + pick(t), 0);
  const losing = tasks.filter((t) => t.grossMargin < 0).length;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered autoHeight>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem' }}>
          Cost breakdown — {title}
          {ready && tasks.length > 0 && (
            <div className="tw-text-content-muted tw-font-normal" style={{ fontSize: '0.85rem' }}>
              {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'} · {losing} at a loss
            </div>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="tw-flex tw-justify-center tw-py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {ready && tasks.length === 0 && (
          <p className="tw-text-content-muted tw-text-center tw-py-3">No tasks found for this activity.</p>
        )}
        {ready && tasks.length > 0 && (
          <Table striped bordered hover size="sm" style={{ fontSize: '0.8rem', marginBottom: 0 }}>
            <thead>
              <tr style={{ backgroundColor: REPORT_HEADER_BG }}>
                <th title="Task #">Task #</th>
                <th title="Name">Name</th>
                <th style={{ textAlign: 'right' }} title="Value ($)">Value</th>
                {COST_COLUMNS.map((c) => (
                  <th key={c.key} style={{ textAlign: 'right' }} title={`${c.title} ($)`}>{c.label}</th>
                ))}
                <th style={{ textAlign: 'right' }} title="Total cost ($)">Cost</th>
                <th style={{ textAlign: 'right' }} title="Gross margin ($)">GM</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.taskId}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Link to={qpTrackingTaskUrl(task.taskId)} title="Open task" className="tw-text-primary tw-underline">
                      {task.taskNumber || task.taskId}
                    </Link>
                  </td>
                  <td>{task.taskName || '—'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{money(task.value)}</td>
                  {COST_COLUMNS.map((c) => (
                    <td key={c.key} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{money(task.costs[c.key])}</td>
                  ))}
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{money(task.costs.total)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap', color: task.grossMargin < 0 ? '#dc3545' : undefined }}>
                    {money(task.grossMargin)}
                  </td>
                </tr>
              ))}
            </tbody>
            {tasks.length > 1 && (
              <tfoot>
                <tr style={{ fontWeight: 'bold', backgroundColor: REPORT_HEADER_BG }}>
                  <td colSpan={2}>Total</td>
                  <td style={{ textAlign: 'right' }}>{money(sum((t) => t.value))}</td>
                  {COST_COLUMNS.map((c) => (
                    <td key={c.key} style={{ textAlign: 'right' }}>{money(sum((t) => t.costs[c.key]))}</td>
                  ))}
                  <td style={{ textAlign: 'right' }}>{money(sum((t) => t.costs.total))}</td>
                  <td style={{ textAlign: 'right', color: sum((t) => t.grossMargin) < 0 ? '#dc3545' : undefined }}>
                    {money(sum((t) => t.grossMargin))}
                  </td>
                </tr>
              </tfoot>
            )}
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CostBreakdownModal;
